<template>
  <transition name="lm-dialog">
    <div v-if="state.show" class="lm-dialog-overlay" @click.self="cancel">
      <div class="lm-dialog" role="dialog" aria-modal="true">
        <div class="lm-dialog-title">{{ state.title }}</div>
        <div class="lm-dialog-msg">{{ state.message }}</div>
        <div v-if="state.showInput" class="lm-dialog-input-wrap">
          <input
            ref="inputEl"
            v-model="inputValue"
            class="lm-dialog-input"
            :placeholder="state.placeholder || '请输入'"
            @keyup.enter="confirm"
          />
        </div>
        <div class="lm-dialog-actions">
          <button class="lm-dialog-btn lm-dialog-cancel" @click="cancel">{{ state.cancelText || '取消' }}</button>
          <button class="lm-dialog-btn lm-dialog-ok" @click="confirm">{{ state.okText || '确定' }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { nextTick, reactive, ref, watch } from 'vue';
import { toast } from '../utils/toast';

// 全站禁用原生 alert：统一走轻量 toast，避免浏览器默认弹窗
if (typeof window !== 'undefined' && !window.__lmAlertPatched) {
  window.__lmAlertPatched = true;
  const nativeAlert = window.alert;
  window.alert = (msg) => {
    toast.info(String(msg == null ? '' : msg));
    return undefined;
  };
  void nativeAlert;
}

// 模块级状态，全站共享同一个确认弹窗实例
const state = reactive({
  show: false,
  title: '提示',
  message: '',
  okText: '',
  cancelText: '',
  showInput: false,
  placeholder: ''
});

let resolver = null;
const inputValue = ref('');

export const confirmDialog = (options = {}) => {
  state.title = options.title || '提示';
  state.message = options.message || '';
  state.okText = options.okText || '确定';
  state.cancelText = options.cancelText || '取消';
  state.showInput = !!options.showInput;
  state.placeholder = options.placeholder || '';
  inputValue.value = options.defaultValue || '';
  state.show = true;
  return new Promise(resolve => { resolver = resolve; });
};

const settle = (result) => {
  state.show = false;
  if (resolver) { resolver(result); resolver = null; }
};

export default {
  name: 'GlobalConfirmDialog',
  setup() {
    const inputEl = ref(null);

    watch(() => state.show, (v) => {
      if (v && state.showInput) {
        nextTick(() => { inputEl.value && inputEl.value.focus(); });
      }
    });

    const confirm = () => {
      if (state.showInput) {
        settle(inputValue.value);
      } else {
        settle(true);
      }
    };

    const cancel = () => {
      if (state.showInput) {
        settle(null);
      } else {
        settle(false);
      }
    };

    return { state, inputValue, inputEl, confirm, cancel };
  }
};
</script>

<style scoped>
.lm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99998;
  backdrop-filter: blur(2px);
}

.lm-dialog {
  width: 92%;
  max-width: 380px;
  background: #fff;
  border-radius: 14px;
  padding: 24px 22px 18px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.lm-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
}

.lm-dialog-msg {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  word-break: break-word;
  margin-bottom: 6px;
}

.lm-dialog-input-wrap {
  margin: 14px 0 4px;
}

.lm-dialog-input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e3e6eb;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  text-align: center;
}

.lm-dialog-input:focus {
  border-color: #ff9600;
}

.lm-dialog-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.lm-dialog-btn {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.lm-dialog-cancel {
  background: #f2f3f5;
  color: #666;
}

.lm-dialog-cancel:hover {
  background: #e8eaed;
}

.lm-dialog-ok {
  background: linear-gradient(135deg, #ff9600, #ff7d00);
  color: #fff;
}

.lm-dialog-ok:hover {
  background: linear-gradient(135deg, #ff8a00, #ff6d00);
}

.lm-dialog-enter-active, .lm-dialog-leave-active {
  transition: all 0.25s ease;
}

.lm-dialog-enter-from, .lm-dialog-leave-to {
  opacity: 0;
  transform: scale(0.92);
}
</style>
